'use client';

import {
  Avatar,
  Box,
  Button,
  Center,
  Dialog,
  Drawer,
  Flex,
  HStack,
  IconButton,
  Input,
  Portal,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import {
  CalendarClock,
  ChevronLeft,
  Download,
  Ellipsis,
  ExternalLink,
  FileClock,
  MessageSquare,
  Pencil,
  RefreshCw,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { SidePanelToggleButton } from '@/components/core/app-layout/SidePanelToggleButton';
import { useDownloads } from '@/components/downloads/DownloadsProvider';
import { useAuth } from '@/app/auth-provider';
import { AppShellToolbarPortal } from '@/components/core/app-layout/AppShellToolbar';
import { LoadingState } from '@/components/shared/ui/LoadingState';
import { Menu } from '@chakra-ui/react';
import { formatCodexRelativeTime } from '@/lib/codex-workspace-format';
import { markReportContentSaved, readReportSaveState } from '@/lib/reports/client-save-state';
import type {
  LivingReportRecord,
  ReportCommentTarget,
  ReportCommentTextAnchor,
  ReportCommentThread,
  ReportContent,
  ReportVersionSummary,
} from '@/lib/reports/types';
import type { ReportCollaborationConfig } from './ReportEditor';

const ReportEditor = dynamic(() => import('./ReportEditor').then(module => module.ReportEditor), {
  ssr: false,
  loading: () => (
    <Box minH="620px">
      <LoadingState message="Opening editor…" minH="620px" />
    </Box>
  ),
});

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const REPORT_EXPORT_OPTIONS = [
  { format: 'pdf', label: 'PDF document' },
  { format: 'docx', label: 'Word document (.docx)' },
  { format: 'markdown', label: 'Markdown (.md)' },
  { format: 'html', label: 'Web page (.html)' },
] as const;
type ReportExportFormat = (typeof REPORT_EXPORT_OPTIONS)[number]['format'];

interface ScheduleRecord {
  id: string;
  status: string;
  trigger: { type: string; day_of_week?: number; day_of_month?: number };
}

interface RefreshScanRunRecord {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  error?: string;
  output_report_id?: string;
  report_update_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'exhausted';
  report_update_error?: string;
}

interface ReportAccessMetadata {
  canManage: boolean;
}

interface ReportSaveIntent {
  reportId: string;
  snapshot: boolean;
  includeContent: boolean;
}

interface ReportSaveWaiter {
  resolve: () => void;
  reject: (error: Error) => void;
}

interface ReportRecovery {
  version: 1;
  reportId: string;
  userId: string;
  title: string;
  content: ReportContent;
  baseTitle: string;
  baseContent: ReportContent;
  clientSaveId: string;
  clientSaveRevision: number;
}

const REFRESH_POLL_TIMEOUT_MS = 15 * 60 * 1000;
const REPORT_SAVE_SESSION_KEY = 'attune:living-report-save-client';
const REPORT_RECOVERY_KEY_PREFIX = 'attune:living-report-recovery:';
let fallbackSaveClient = { id: '', revision: 0 };

function reportRecoveryKey(reportId: string, userId: string): string {
  return `${REPORT_RECOVERY_KEY_PREFIX}${encodeURIComponent(userId)}:${encodeURIComponent(reportId)}`;
}

function readReportRecovery(reportId: string, userId: string): ReportRecovery | null {
  try {
    const key = reportRecoveryKey(reportId, userId);
    const stored = window.localStorage.getItem(key);
    if (!stored) return null;
    const recovery = JSON.parse(stored) as Partial<ReportRecovery>;
    if (
      recovery.version !== 1 ||
      recovery.reportId !== reportId ||
      recovery.userId !== userId ||
      typeof recovery.title !== 'string' ||
      !recovery.content ||
      typeof recovery.content !== 'object' ||
      typeof recovery.baseTitle !== 'string' ||
      !recovery.baseContent ||
      typeof recovery.baseContent !== 'object' ||
      typeof recovery.clientSaveId !== 'string' ||
      !/^[A-Za-z0-9_-]{8,100}$/.test(recovery.clientSaveId) ||
      !Number.isSafeInteger(recovery.clientSaveRevision) ||
      Number(recovery.clientSaveRevision) < 1
    ) {
      window.localStorage.removeItem(key);
      return null;
    }
    return recovery as ReportRecovery;
  } catch {
    return null;
  }
}

function persistReportRecovery(recovery: ReportRecovery): void {
  try {
    window.localStorage.setItem(
      reportRecoveryKey(recovery.reportId, recovery.userId),
      JSON.stringify(recovery)
    );
  } catch (error) {
    console.warn('[living-reports] Failed to persist report recovery', error);
  }
}

function clearReportRecovery(
  reportId: string,
  userId: string,
  clientSave: { clientSaveId: string; clientSaveRevision: number }
): void {
  const recovery = readReportRecovery(reportId, userId);
  if (
    recovery?.clientSaveId !== clientSave.clientSaveId ||
    recovery.clientSaveRevision > clientSave.clientSaveRevision
  )
    return;
  try {
    window.localStorage.removeItem(reportRecoveryKey(reportId, userId));
  } catch {
    // The accepted save remains authoritative even if storage cleanup is unavailable.
  }
}

function nextReportClientSave(minRevision = 0): {
  clientSaveId: string;
  clientSaveRevision: number;
} {
  let state = fallbackSaveClient;
  try {
    const stored = window.sessionStorage.getItem(REPORT_SAVE_SESSION_KEY);
    const parsed = stored ? (JSON.parse(stored) as { id?: unknown; revision?: unknown }) : null;
    if (
      parsed &&
      typeof parsed.id === 'string' &&
      /^[A-Za-z0-9_-]{8,100}$/.test(parsed.id) &&
      Number.isSafeInteger(Number(parsed.revision))
    ) {
      const storedState = { id: parsed.id, revision: Math.max(0, Number(parsed.revision)) };
      state =
        fallbackSaveClient.id === storedState.id &&
        fallbackSaveClient.revision > storedState.revision
          ? fallbackSaveClient
          : storedState;
    }
  } catch {
    // sessionStorage can be unavailable in hardened browser contexts.
  }
  if (!state.id) {
    const nonce = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    state = { id: `report-save-${nonce}`.replace(/[^A-Za-z0-9_-]/g, '-'), revision: 0 };
  }
  state = { id: state.id, revision: Math.max(state.revision, minRevision) + 1 };
  fallbackSaveClient = state;
  try {
    window.sessionStorage.setItem(REPORT_SAVE_SESSION_KEY, JSON.stringify(state));
  } catch {
    // The in-memory fallback still orders requests for this page lifetime.
  }
  return { clientSaveId: state.id, clientSaveRevision: state.revision };
}

interface LivingReportClientProps {
  reportId: string;
  embedded?: boolean;
  embeddedNavigation?: ReactNode;
  embeddedActions?: ReactNode;
  onClose?: () => void;
  onArchived?: () => void;
  onUnresolvedCommentsChange?: (count: number) => void;
}

export function LivingReportClient({
  reportId,
  embedded = false,
  embeddedNavigation,
  embeddedActions,
  onClose,
  onArchived,
  onUnresolvedCommentsChange,
}: LivingReportClientProps) {
  const { user } = useAuth();
  const { run: runDownload } = useDownloads();
  const router = useRouter();
  const [report, setReport] = useState<LivingReportRecord | null>(null);
  const [canManageReport, setCanManageReport] = useState(false);
  const [content, setContent] = useState<ReportContent | null>(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadRetryTick, setLoadRetryTick] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [collaboration, setCollaboration] = useState<ReportCollaborationConfig | null>(null);
  const [collaborationStatus, setCollaborationStatus] = useState<
    'local' | 'connecting' | 'connected' | 'offline'
  >('local');
  const [, setPresenceUsers] = useState<Array<{ id: string; name: string; color: string }>>([]);
  const [versions, setVersions] = useState<ReportVersionSummary[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [comments, setComments] = useState<ReportCommentThread[]>([]);
  const [commentBlockId, setCommentBlockId] = useState<string | null>(null);
  const [commentAnchor, setCommentAnchor] = useState<ReportCommentTextAnchor | null>(null);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [embeddedCommentsOpen, setEmbeddedCommentsOpen] = useState(false);
  const [commentReplies, setCommentReplies] = useState<Record<string, string>>({});
  const [commentSaving, setCommentSaving] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleRecord | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleType, setScheduleType] = useState<'manual' | 'weekly_scan' | 'monthly_scan'>(
    'weekly_scan'
  );
  const [scheduleDay, setScheduleDay] = useState(1);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [refreshRunId, setRefreshRunId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<ReportExportFormat | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapshotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef('');
  const lastSnapshotRef = useRef('');
  const contentRef = useRef<ReportContent | null>(null);
  const liveContentReaderRef = useRef<(() => ReportContent | null) | null>(null);
  const titleRef = useRef('');
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const activeReportIdRef = useRef(reportId);
  activeReportIdRef.current = reportId;
  const authUserRef = useRef(user);
  authUserRef.current = user;
  const userId = user?.uid || null;
  const loadGenerationRef = useRef(0);
  const pendingRestSaveRef = useRef(false);
  const flushPendingRestSaveRef = useRef<(targetReportId: string) => void>(() => undefined);
  const queuedSaveIntentRef = useRef<ReportSaveIntent | null>(null);
  const saveQueueRunningRef = useRef(false);
  const saveQueueWaitersRef = useRef<ReportSaveWaiter[]>([]);
  const performQueuedSaveRef = useRef<(intent: ReportSaveIntent) => Promise<void>>(async () => {});
  const runSaveQueueRef = useRef<() => Promise<void>>(async () => {});
  const authTokenRef = useRef<string | null>(null);
  const refreshStartedAtRef = useRef<number | null>(null);
  const unresolvedCommentCount = comments.reduce(
    (count, comment) => count + (comment.resolved ? 0 : 1),
    0
  );
  const commentsPaneOpen = embeddedCommentsOpen;
  const setCommentsPaneOpen = setEmbeddedCommentsOpen;
  const activeCommentCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!commentsPaneOpen || !activeCommentId) return;
    const frame = requestAnimationFrame(() => {
      activeCommentCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeCommentId, commentsPaneOpen]);

  useEffect(() => {
    onUnresolvedCommentsChange?.(unresolvedCommentCount);
  }, [onUnresolvedCommentsChange, unresolvedCommentCount]);

  useEffect(() => {
    authTokenRef.current = null;
  }, [user?.uid]);

  const authorizedFetch = useCallback(async (input: RequestInfo | URL, init: RequestInit = {}) => {
    const currentUser = authUserRef.current;
    if (!currentUser) throw new Error('Not authenticated');
    const token = await currentUser.getIdToken();
    authTokenRef.current = token;
    return fetch(input, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  }, []);

  const loadReport = useCallback(
    async (isCurrentLoad: () => boolean) => {
      if (!userId) return;
      const response = await authorizedFetch(`/api/reports/${encodeURIComponent(reportId)}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Report not found');
      if (!isCurrentLoad()) return;
      let nextReport = payload.report as LivingReportRecord;
      setCanManageReport(
        (payload.access as Partial<ReportAccessMetadata> | undefined)?.canManage === true
      );
      const storedRecovery = readReportRecovery(reportId, userId);
      if (storedRecovery) {
        let recovery = storedRecovery;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          if (!isCurrentLoad()) return;
          const recoveryResponse = await authorizedFetch(
            `/api/reports/${encodeURIComponent(reportId)}`,
            {
              method: 'PATCH',
              body: JSON.stringify({
                title: recovery.title,
                content: recovery.content,
                snapshot: false,
                clientSaveId: recovery.clientSaveId,
                clientSaveRevision: recovery.clientSaveRevision,
                mergeBaseTitle: recovery.baseTitle,
                mergeBaseContent: recovery.baseContent,
              }),
            }
          );
          const recoveryPayload = await recoveryResponse.json().catch(() => ({}));
          if (!isCurrentLoad()) return;
          if (!recoveryResponse.ok) {
            if (recoveryResponse.status === 400) {
              clearReportRecovery(reportId, userId, recovery);
              setSaveError(
                'A damaged local recovery could not be restored; the server copy was loaded.'
              );
              break;
            }
            throw new Error(recoveryPayload.error || 'Failed to recover unsaved report edits');
          }
          if (recoveryPayload.report) nextReport = recoveryPayload.report as LivingReportRecord;
          if (recoveryPayload.stale !== true || recoveryPayload.reconciled === true) {
            clearReportRecovery(reportId, userId, recovery);
            break;
          }
          const storedRevision = Math.max(0, Number(recoveryPayload.storedRevision) || 0);
          const nextClientSave = nextReportClientSave(storedRevision);
          recovery = { ...recovery, ...nextClientSave };
          persistReportRecovery(recovery);
          if (attempt === 1) {
            setSaveError('Pending local edits remain safe and will retry.');
          }
        }
      }
      if (!isCurrentLoad()) return;
      setReport(nextReport);
      setContent(nextReport.content);
      setTitle(nextReport.title);
      contentRef.current = nextReport.content;
      titleRef.current = nextReport.title;
      const serialized = JSON.stringify({ title: nextReport.title, content: nextReport.content });
      lastSavedRef.current = serialized;
      lastSnapshotRef.current = serialized;
      pendingRestSaveRef.current = false;
    },
    [authorizedFetch, reportId, userId]
  );

  const loadSupportingData = useCallback(
    async (isCurrentLoad: () => boolean) => {
      if (!userId) return;
      const [tokenResponse, commentsResponse, scheduleResponse] = await Promise.all([
        authorizedFetch(`/api/reports/${encodeURIComponent(reportId)}/collab-token`, {
          method: 'POST',
        }).catch(() => null),
        authorizedFetch(`/api/reports/${encodeURIComponent(reportId)}/comments`).catch(() => null),
        authorizedFetch(`/api/reports/${encodeURIComponent(reportId)}/schedule`).catch(() => null),
      ]);
      if (!isCurrentLoad()) return;
      if (tokenResponse?.ok) {
        const payload = await tokenResponse.json();
        if (!isCurrentLoad()) return;
        setCollaboration({
          ...(payload as ReportCollaborationConfig),
          getToken: async () => {
            const response = await authorizedFetch(
              `/api/reports/${encodeURIComponent(reportId)}/collab-token`,
              { method: 'POST' }
            );
            const refreshed = await response.json().catch(() => ({}));
            if (!response.ok || !refreshed.token)
              throw new Error(refreshed.error || 'Failed to refresh collaboration access');
            return String(refreshed.token);
          },
        });
      }
      if (commentsResponse?.ok) {
        const payload = await commentsResponse.json();
        if (!isCurrentLoad()) return;
        setComments(Array.isArray(payload.comments) ? payload.comments : []);
      }
      if (scheduleResponse?.ok) {
        const payload = await scheduleResponse.json();
        if (!isCurrentLoad()) return;
        const nextSchedule = payload.automation || null;
        setSchedule(nextSchedule);
        if (nextSchedule?.trigger?.type === 'weekly_scan') {
          setScheduleType('weekly_scan');
          setScheduleDay(nextSchedule.trigger.day_of_week ?? 1);
        } else if (nextSchedule?.trigger?.type === 'monthly_scan') {
          setScheduleType('monthly_scan');
          setScheduleDay(nextSchedule.trigger.day_of_month ?? 1);
        } else if (nextSchedule) {
          setScheduleType('manual');
        }
      }
    },
    [authorizedFetch, reportId, userId]
  );

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const loadGeneration = loadGenerationRef.current + 1;
    loadGenerationRef.current = loadGeneration;
    const isCurrentLoad = () =>
      !cancelled &&
      activeReportIdRef.current === reportId &&
      loadGenerationRef.current === loadGeneration;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setScheduleSaving(false);
    setCommentSaving(false);
    setDeleting(false);
    setReport(null);
    setCanManageReport(false);
    setContent(null);
    setTitle('');
    contentRef.current = null;
    titleRef.current = '';
    setCollaboration(null);
    setCollaborationStatus('local');
    setPresenceUsers([]);
    setVersions([]);
    setHistoryOpen(false);
    setComments([]);
    setCommentBlockId(null);
    setCommentAnchor(null);
    setActiveCommentId(null);
    setCommentDraft('');
    setCommentReplies({});
    setSchedule(null);
    setScheduleOpen(false);
    setScheduleType('weekly_scan');
    setScheduleDay(1);
    setRefreshRunId(null);
    refreshStartedAtRef.current = null;
    Promise.all([loadReport(isCurrentLoad), loadSupportingData(isCurrentLoad)])
      .catch(error => {
        if (isCurrentLoad()) {
          setLoadError(error instanceof Error ? error.message : 'Failed to load report');
        }
      })
      .finally(() => {
        if (isCurrentLoad()) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadReport, loadRetryTick, loadSupportingData, reportId, userId]);

  flushPendingRestSaveRef.current = (targetReportId: string) => {
    const currentContent = contentRef.current;
    if (!pendingRestSaveRef.current || !currentContent || !user) return;
    pendingRestSaveRef.current = false;
    const currentTitle = titleRef.current;
    const clientSave = nextReportClientSave();
    const base = readReportSaveState(lastSavedRef.current, {
      title: currentTitle,
      content: currentContent,
    });
    const body = JSON.stringify({
      title: currentTitle,
      content: currentContent,
      snapshot: false,
      ...clientSave,
      mergeBaseTitle: base.title,
      mergeBaseContent: base.content,
    });
    const canKeepAlive = new TextEncoder().encode(body).byteLength <= 60_000;
    persistReportRecovery({
      version: 1,
      reportId: targetReportId,
      userId: user.uid,
      title: currentTitle,
      content: currentContent,
      baseTitle: base.title,
      baseContent: base.content,
      ...clientSave,
    });
    const sendFlush = async (token: string) => {
      try {
        const response = await fetch(`/api/reports/${encodeURIComponent(targetReportId)}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body,
          keepalive: canKeepAlive,
        });
        if (!response.ok) {
          pendingRestSaveRef.current = true;
          return response;
        }
        const payload = (await response
          .clone()
          .json()
          .catch(() => null)) as {
          stale?: boolean;
          reconciled?: boolean;
        } | null;
        if (payload && (payload.stale !== true || payload.reconciled === true)) {
          clearReportRecovery(targetReportId, user.uid, clientSave);
        } else {
          pendingRestSaveRef.current = true;
        }
        return response;
      } catch (error) {
        pendingRestSaveRef.current = true;
        throw error;
      }
    };
    const cachedToken = authTokenRef.current;
    if (cachedToken) {
      void sendFlush(cachedToken).catch(error => {
        console.warn('[living-reports] Failed to flush pending report edits', error);
      });
      return;
    }
    void user
      .getIdToken()
      .then(token => sendFlush(token))
      .catch(error => {
        console.warn('[living-reports] Failed to flush pending report edits', error);
      });
  };

  performQueuedSaveRef.current = async intent => {
    if (intent.reportId !== activeReportIdRef.current) return;
    const currentContent =
      collaborationStatus === 'connected'
        ? liveContentReaderRef.current?.() || contentRef.current
        : contentRef.current;
    const currentTitle = titleRef.current;
    if (!report || !currentContent || !user) return;
    const serialized = JSON.stringify({ title: currentTitle, content: currentContent });
    const restSnapshot = intent.snapshot;
    const collaborationSnapshot = restSnapshot && Boolean(collaboration);
    const restIncludesContent = intent.includeContent && !collaboration;
    const writesRestContent = restIncludesContent || (restSnapshot && !collaboration);
    const queuedContentWrite = () =>
      Boolean(queuedSaveIntentRef.current?.includeContent || queuedSaveIntentRef.current?.snapshot);
    if (!restSnapshot && serialized === lastSavedRef.current) {
      pendingRestSaveRef.current = queuedContentWrite();
      return;
    }
    if (restSnapshot && serialized === lastSnapshotRef.current) {
      if (writesRestContent) pendingRestSaveRef.current = queuedContentWrite();
      return;
    }
    if (writesRestContent) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setSaveError(null);
    try {
      const clientSave = nextReportClientSave();
      const response = await authorizedFetch(
        `/api/reports/${encodeURIComponent(intent.reportId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            title: currentTitle,
            ...(writesRestContent ? { content: currentContent } : {}),
            snapshot: restSnapshot,
            collaborationSnapshot,
            summary: 'Saved collaborative edits',
            ...clientSave,
          }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Failed to save report');
      if (payload.stale !== true || payload.reconciled === true) {
        clearReportRecovery(intent.reportId, user.uid, clientSave);
      }

      const latestContent =
        collaborationStatus === 'connected'
          ? liveContentReaderRef.current?.() || contentRef.current
          : contentRef.current;
      const latestSerialized = JSON.stringify({
        title: titleRef.current,
        content: latestContent,
      });
      const stillSameReport = intent.reportId === activeReportIdRef.current;
      const versionSerialized = payload.version
        ? JSON.stringify({
            title: payload.version.title,
            content: payload.version.content,
          })
        : null;
      const responseIsCurrent =
        payload.stale !== true &&
        stillSameReport &&
        (payload.version
          ? true
          : collaboration
            ? currentTitle === titleRef.current
            : serialized === latestSerialized);

      if (stillSameReport && !responseIsCurrent) {
        pendingRestSaveRef.current = true;
        const queued = queuedSaveIntentRef.current;
        const sameQueuedReport = queued?.reportId === intent.reportId;
        queuedSaveIntentRef.current = {
          reportId: intent.reportId,
          snapshot: restSnapshot || Boolean(sameQueuedReport && queued?.snapshot),
          includeContent: writesRestContent || Boolean(sameQueuedReport && queued?.includeContent),
        };
        return;
      }

      if (!stillSameReport) return;

      const canonicalSavedTitle =
        (payload.report as LivingReportRecord | undefined)?.title || currentTitle;
      pendingRestSaveRef.current = queuedContentWrite() || titleRef.current !== canonicalSavedTitle;
      if (titleRef.current === currentTitle && canonicalSavedTitle !== currentTitle) {
        titleRef.current = canonicalSavedTitle;
        setTitle(canonicalSavedTitle);
      }
      lastSavedRef.current = JSON.stringify({
        title: canonicalSavedTitle,
        content: collaboration ? latestContent : currentContent,
      });
      if (payload.version) lastSnapshotRef.current = versionSerialized || serialized;
      setReport(payload.report);
      if (payload.version && historyOpen) {
        setVersions(current => [
          payload.version,
          ...current.filter(version => version.id !== payload.version.id),
        ]);
      }
    } catch (error) {
      pendingRestSaveRef.current = true;
      const message = error instanceof Error ? error.message : 'Failed to save report';
      setSaveError(message);
      throw error instanceof Error ? error : new Error(message);
    }
  };

  runSaveQueueRef.current = async () => {
    try {
      while (queuedSaveIntentRef.current) {
        const intent = queuedSaveIntentRef.current;
        queuedSaveIntentRef.current = null;
        await performQueuedSaveRef.current(intent);
      }
      const waiters = saveQueueWaitersRef.current.splice(0);
      waiters.forEach(waiter => waiter.resolve());
    } catch (error) {
      queuedSaveIntentRef.current = null;
      const failure = error instanceof Error ? error : new Error('Failed to save report');
      const waiters = saveQueueWaitersRef.current.splice(0);
      waiters.forEach(waiter => waiter.reject(failure));
    } finally {
      saveQueueRunningRef.current = false;
      if (queuedSaveIntentRef.current) {
        saveQueueRunningRef.current = true;
        void runSaveQueueRef.current();
      }
    }
  };

  const save = useCallback((snapshot = false, includeContent = true): Promise<void> => {
    const queued = queuedSaveIntentRef.current;
    const targetReportId = activeReportIdRef.current;
    const sameReport = queued?.reportId === targetReportId;
    queuedSaveIntentRef.current = {
      reportId: targetReportId,
      snapshot: snapshot || Boolean(sameReport && queued?.snapshot),
      includeContent: includeContent || Boolean(sameReport && queued?.includeContent),
    };
    const completion = new Promise<void>((resolve, reject) => {
      saveQueueWaitersRef.current.push({ resolve, reject });
    });
    if (!saveQueueRunningRef.current) {
      saveQueueRunningRef.current = true;
      void runSaveQueueRef.current();
    }
    return completion;
  }, []);

  const scheduleSave = useCallback(
    (nextContent: ReportContent) => {
      setContent(nextContent);
      contentRef.current = nextContent;
      if (collaboration) {
        lastSavedRef.current = markReportContentSaved(
          lastSavedRef.current,
          nextContent,
          titleRef.current
        );
        return;
      }
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      pendingRestSaveRef.current = true;
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        void save(false, true).catch(error => {
          toast.error(error instanceof Error ? error.message : 'Failed to save report');
        });
      }, 1_500);
      if (!snapshotTimerRef.current) {
        snapshotTimerRef.current = setTimeout(
          () => {
            snapshotTimerRef.current = null;
            void save(true, true).catch(error => {
              toast.error(error instanceof Error ? error.message : 'Failed to save report');
            });
          },
          5 * 60 * 1000
        );
      }
    },
    [collaboration, save]
  );

  useEffect(() => {
    if (!collaboration) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
    const saved = readReportSaveState(lastSavedRef.current, {
      title: titleRef.current,
      content: contentRef.current || { type: 'doc', content: [] },
    });
    pendingRestSaveRef.current = titleRef.current !== saved.title;
    saveTimerRef.current = null;
    snapshotTimerRef.current = null;
  }, [collaboration]);

  useEffect(() => {
    if (!userId || !report) return;
    let cancelled = false;
    const loadComments = () => {
      void authorizedFetch(`/api/reports/${encodeURIComponent(reportId)}/comments`)
        .then(response => response.json().then(payload => ({ ok: response.ok, payload })))
        .then(({ ok, payload }) => {
          if (
            !cancelled &&
            activeReportIdRef.current === reportId &&
            ok &&
            Array.isArray(payload.comments)
          ) {
            setComments(payload.comments);
          }
        })
        .catch(() => undefined);
    };
    const interval = window.setInterval(loadComments, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [authorizedFetch, report, reportId, userId]);

  useEffect(() => {
    if (!userId || !report) return;
    let cancelled = false;
    const interval = window.setInterval(() => {
      void authorizedFetch(`/api/reports/${encodeURIComponent(reportId)}`)
        .then(response => response.json().then(payload => ({ ok: response.ok, payload })))
        .then(({ ok, payload }) => {
          if (cancelled || activeReportIdRef.current !== reportId || !ok || !payload.report) return;
          const nextReport = payload.report as LivingReportRecord;
          setCanManageReport(
            (payload.access as Partial<ReportAccessMetadata> | undefined)?.canManage === true
          );
          if (
            nextReport.currentVersion <= report.currentVersion &&
            nextReport.updatedAt === report.updatedAt
          )
            return;
          if (collaboration) {
            setReport(nextReport);
            if (titleRef.current === report.title && nextReport.title !== report.title) {
              setTitle(nextReport.title);
              titleRef.current = nextReport.title;
              lastSavedRef.current = JSON.stringify({
                title: nextReport.title,
                content: contentRef.current,
              });
            }
            return;
          }
          const hasUnsavedEdits =
            JSON.stringify({
              title: titleRef.current,
              content: contentRef.current,
            }) !== lastSavedRef.current;
          if (hasUnsavedEdits) return;
          setReport(nextReport);
          setContent(nextReport.content);
          setTitle(nextReport.title);
          contentRef.current = nextReport.content;
          titleRef.current = nextReport.title;
          lastSavedRef.current = JSON.stringify({
            title: nextReport.title,
            content: nextReport.content,
          });
          lastSnapshotRef.current = lastSavedRef.current;
        })
        .catch(() => undefined);
    }, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [authorizedFetch, collaboration, report, reportId, userId]);

  useEffect(() => {
    const activeReportId = reportId;
    const flushPendingSave = () => flushPendingRestSaveRef.current(activeReportId);
    window.addEventListener('pagehide', flushPendingSave);
    return () => {
      window.removeEventListener('pagehide', flushPendingSave);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
      saveTimerRef.current = null;
      snapshotTimerRef.current = null;
      flushPendingSave();
    };
  }, [reportId]);

  useEffect(() => {
    if (!refreshRunId || !userId || !report?.projectId) return;
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const pollRefresh = async () => {
      try {
        const [reportResponse, runResponse] = await Promise.all([
          authorizedFetch(`/api/reports/${encodeURIComponent(reportId)}`),
          authorizedFetch('/api/attune/scan-runs/detail', {
            method: 'POST',
            body: JSON.stringify({ projectId: report.projectId, runId: refreshRunId }),
          }),
        ]);
        const [reportPayload, runPayload] = await Promise.all([
          reportResponse.json().catch(() => ({})),
          runResponse.json().catch(() => ({})),
        ]);
        if (cancelled || activeReportIdRef.current !== reportId) return;
        const nextReport = reportResponse.ok
          ? (reportPayload.report as LivingReportRecord | undefined)
          : undefined;
        const run = runResponse.ok
          ? (runPayload.run as RefreshScanRunRecord | undefined)
          : undefined;

        if (
          run?.status === 'failed' ||
          run?.report_update_status === 'failed' ||
          run?.report_update_status === 'exhausted'
        ) {
          if (nextReport) setReport(nextReport);
          refreshStartedAtRef.current = null;
          setRefreshRunId(null);
          toast.error(
            run.report_update_error ||
              run.error ||
              'Background refresh failed before the report could update.'
          );
          return;
        }

        if (
          run?.status === 'completed' &&
          run.report_update_status === 'completed' &&
          run.output_report_id
        ) {
          refreshStartedAtRef.current = null;
          setRefreshRunId(null);
          toast.success('New Doc created.');
          router.push(`/app/docs/${encodeURIComponent(run.output_report_id)}`);
          return;
        }

        if (nextReport?.lastRunId === refreshRunId) {
          setReport(nextReport);
          if (!collaboration) {
            setContent(nextReport.content);
            setTitle(nextReport.title);
            contentRef.current = nextReport.content;
            titleRef.current = nextReport.title;
            lastSavedRef.current = JSON.stringify({
              title: nextReport.title,
              content: nextReport.content,
            });
            lastSnapshotRef.current = lastSavedRef.current;
          } else if (titleRef.current === report.title && nextReport.title !== report.title) {
            setTitle(nextReport.title);
            titleRef.current = nextReport.title;
          }
          refreshStartedAtRef.current = null;
          setRefreshRunId(null);
          toast.success('Report refreshed.');
          return;
        }

        const startedAt = refreshStartedAtRef.current;
        if (startedAt && Date.now() - startedAt >= REFRESH_POLL_TIMEOUT_MS) {
          refreshStartedAtRef.current = null;
          setRefreshRunId(null);
          toast.error('Doc generation is taking longer than expected. Check Background Tasks.');
          return;
        }
      } catch {
        if (cancelled || activeReportIdRef.current !== reportId) return;
        const startedAt = refreshStartedAtRef.current;
        if (startedAt && Date.now() - startedAt >= REFRESH_POLL_TIMEOUT_MS) {
          refreshStartedAtRef.current = null;
          setRefreshRunId(null);
          toast.error('Could not confirm the new Doc. Check Background Tasks.');
          return;
        }
      }
      if (!cancelled) timeout = setTimeout(() => void pollRefresh(), 5_000);
    };
    void pollRefresh();
    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [authorizedFetch, collaboration, refreshRunId, report, reportId, router, userId]);

  const openHistory = async () => {
    if (!report) return;
    const targetReportId = report.id;
    try {
      await save(true);
      if (activeReportIdRef.current !== targetReportId) return;
      setHistoryOpen(true);
      const response = await authorizedFetch(
        `/api/reports/${encodeURIComponent(targetReportId)}/versions`
      );
      const payload = await response.json().catch(() => ({}));
      if (activeReportIdRef.current !== targetReportId) return;
      if (!response.ok) throw new Error(payload.error || 'Failed to load history');
      setVersions(Array.isArray(payload.versions) ? payload.versions : []);
    } catch (error) {
      if (activeReportIdRef.current === targetReportId) {
        toast.error(error instanceof Error ? error.message : 'Failed to load history');
      }
    }
  };

  const restoreVersion = async (version: ReportVersionSummary) => {
    if (!report) return;
    const targetReportId = report.id;
    try {
      await save(true);
      if (activeReportIdRef.current !== targetReportId) return;
      const response = await authorizedFetch(
        `/api/reports/${encodeURIComponent(targetReportId)}/versions/${encodeURIComponent(version.id)}/restore`,
        { method: 'POST' }
      );
      const payload = await response.json().catch(() => ({}));
      if (activeReportIdRef.current !== targetReportId) return;
      if (!response.ok) throw new Error(payload.error || 'Failed to restore version');
      setReport(payload.report);
      setTitle(payload.report.title);
      titleRef.current = payload.report.title;
      if (!collaboration || payload.collaborationApplied === false) {
        if (payload.collaborationApplied === false) setCollaboration(null);
        setContent(payload.report.content);
        contentRef.current = payload.report.content;
        lastSavedRef.current = JSON.stringify({
          title: payload.report.title,
          content: payload.report.content,
        });
        lastSnapshotRef.current = lastSavedRef.current;
      }
      setHistoryOpen(false);
      toast.success(`Restored version ${version.version}.`);
    } catch (error) {
      if (activeReportIdRef.current === targetReportId) {
        toast.error(error instanceof Error ? error.message : 'Failed to restore version');
      }
    }
  };

  const refreshReport = async () => {
    if (!canManageReport || !report || refreshRunId) return;
    const targetReportId = report.id;
    try {
      await save(true);
      if (activeReportIdRef.current !== targetReportId) return;
      const response = await authorizedFetch(
        `/api/reports/${encodeURIComponent(targetReportId)}/refresh`,
        { method: 'POST' }
      );
      const payload = await response.json().catch(() => ({}));
      if (activeReportIdRef.current !== targetReportId) return;
      if (!response.ok) throw new Error(payload.error || 'Failed to start refresh');
      const runId = String(payload.run?.id || '').trim();
      if (!runId) throw new Error('Refresh started without a scan run to monitor');
      refreshStartedAtRef.current = Date.now();
      setRefreshRunId(runId);
      toast.success('Background Doc generation started.');
    } catch (error) {
      if (activeReportIdRef.current !== targetReportId) return;
      refreshStartedAtRef.current = null;
      toast.error(error instanceof Error ? error.message : 'Failed to start refresh');
    }
  };

  const saveSchedule = async () => {
    if (!canManageReport || !report) return;
    const targetReportId = report.id;
    setScheduleSaving(true);
    try {
      await save(true);
      if (activeReportIdRef.current !== targetReportId) return;
      const trigger =
        scheduleType === 'weekly_scan'
          ? { type: scheduleType, day_of_week: scheduleDay }
          : scheduleType === 'monthly_scan'
            ? { type: scheduleType, day_of_month: scheduleDay }
            : { type: 'manual' };
      const response = await authorizedFetch(
        `/api/reports/${encodeURIComponent(targetReportId)}/schedule`,
        {
          method: 'PUT',
          body: JSON.stringify({ trigger }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (activeReportIdRef.current !== targetReportId) return;
      if (!response.ok) throw new Error(payload.error || 'Failed to update schedule');
      setSchedule(payload.automation);
      setReport(payload.report);
      setScheduleOpen(false);
      toast.success(
        scheduleType === 'manual'
          ? 'Report set to manual refresh.'
          : 'Report will keep itself updated.'
      );
    } catch (error) {
      if (activeReportIdRef.current === targetReportId) {
        toast.error(error instanceof Error ? error.message : 'Failed to update schedule');
      }
    } finally {
      if (activeReportIdRef.current === targetReportId) setScheduleSaving(false);
    }
  };

  const requestComment = (target: ReportCommentTarget) => {
    setCommentBlockId(target.blockId);
    setCommentAnchor(target.anchor);
    setActiveCommentId(null);
    setCommentsPaneOpen(true);
  };

  const createComment = async () => {
    if (!report || !commentBlockId || !commentDraft.trim()) return;
    const targetReportId = report.id;
    setCommentSaving(true);
    try {
      const response = await authorizedFetch(
        `/api/reports/${encodeURIComponent(targetReportId)}/comments`,
        {
          method: 'POST',
          body: JSON.stringify({
            blockId: commentBlockId,
            anchor: commentAnchor,
            body: commentDraft.trim(),
          }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (activeReportIdRef.current !== targetReportId) return;
      if (!response.ok) throw new Error(payload.error || 'Failed to add comment');
      setComments(current => [...current, payload.comment]);
      setActiveCommentId(payload.comment.id);
      setCommentAnchor(payload.comment.anchor || null);
      setCommentDraft('');
    } catch (error) {
      if (activeReportIdRef.current === targetReportId) {
        toast.error(error instanceof Error ? error.message : 'Failed to add comment');
      }
    } finally {
      if (activeReportIdRef.current === targetReportId) setCommentSaving(false);
    }
  };

  const replyToComment = async (commentId: string) => {
    if (!report) return;
    const targetReportId = report.id;
    const body = String(commentReplies[commentId] || '').trim();
    if (!body) return;
    try {
      const response = await authorizedFetch(
        `/api/reports/${encodeURIComponent(targetReportId)}/comments/${encodeURIComponent(commentId)}`,
        {
          method: 'POST',
          body: JSON.stringify({ body }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (activeReportIdRef.current !== targetReportId) return;
      if (!response.ok) throw new Error(payload.error || 'Failed to reply');
      setComments(current =>
        current.map(comment => (comment.id === commentId ? payload.comment : comment))
      );
      setCommentReplies(current => ({ ...current, [commentId]: '' }));
    } catch (error) {
      if (activeReportIdRef.current === targetReportId) {
        toast.error(error instanceof Error ? error.message : 'Failed to reply');
      }
    }
  };

  const resolveComment = async (commentId: string) => {
    if (!report) return;
    const targetReportId = report.id;
    try {
      const response = await authorizedFetch(
        `/api/reports/${encodeURIComponent(targetReportId)}/comments/${encodeURIComponent(commentId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ resolved: true }),
        }
      );
      if (activeReportIdRef.current !== targetReportId) return;
      if (!response.ok) throw new Error('Failed to resolve comment');
      setComments(current =>
        current.map(comment =>
          comment.id === commentId ? { ...comment, resolved: true } : comment
        )
      );
      if (activeCommentId === commentId) setActiveCommentId(null);
    } catch (error) {
      if (activeReportIdRef.current === targetReportId) {
        toast.error(error instanceof Error ? error.message : 'Failed to resolve comment');
      }
    }
  };

  const deleteReport = async () => {
    if (!canManageReport || !report || deleting || !window.confirm(`Archive “${report.title}”?`))
      return;
    const targetReportId = report.id;
    setDeleting(true);
    try {
      await save(true);
      if (activeReportIdRef.current !== targetReportId) return;
      const response = await authorizedFetch(`/api/reports/${encodeURIComponent(targetReportId)}`, {
        method: 'DELETE',
      });
      if (activeReportIdRef.current !== targetReportId) return;
      if (!response.ok) throw new Error('Failed to archive report');
      if (embedded) {
        onArchived?.();
      } else {
        router.push('/app/docs');
      }
      toast.success('Doc archived.');
    } catch (error) {
      if (activeReportIdRef.current === targetReportId) {
        toast.error(error instanceof Error ? error.message : 'Failed to archive report');
        setDeleting(false);
      }
    }
  };

  const exportReport = async (format: ReportExportFormat) => {
    if (!report || exportingFormat) return;
    const targetReportId = report.id;
    setExportingFormat(format);
    try {
      await runDownload({ projectId: report.projectId, name: report.title, format }, async () => {
        await save(false, true);
        if (activeReportIdRef.current !== targetReportId) {
          throw new Error('Doc changed before export started. Please try again.');
        }
        return authorizedFetch(
          `/api/reports/${encodeURIComponent(targetReportId)}/export?format=${format}`
        );
      });
      toast.success(`Exported ${report.title}.`);
    } catch (error) {
      if (activeReportIdRef.current === targetReportId) {
        toast.error(error instanceof Error ? error.message : 'Failed to export doc');
      }
    } finally {
      if (activeReportIdRef.current === targetReportId) setExportingFormat(null);
    }
  };

  const collaborationEditingPaused = Boolean(collaboration && collaborationStatus !== 'connected');
  const titleEditor = (
    <HStack gap="0.5" minW={0} flex={embedded ? '1' : undefined}>
      <Input
        ref={titleInputRef}
        aria-label="Doc title"
        title="Edit doc title"
        value={title}
        onChange={event => {
          setTitle(event.target.value);
          titleRef.current = event.target.value;
          pendingRestSaveRef.current = true;
        }}
        onBlur={() => {
          void save(false, collaborationStatus !== 'connected').catch(error => {
            toast.error(error instanceof Error ? error.message : 'Failed to save report');
          });
        }}
        variant="flushed"
        border="0"
        borderRadius="sm"
        px="1.5"
        h="8"
        w={embedded ? 'full' : 'auto'}
        fieldSizing={embedded ? undefined : 'content'}
        minW={embedded ? 0 : '80px'}
        maxW={embedded ? 'none' : { base: '140px', md: '320px' }}
        fontSize="13px"
        fontWeight="550"
        color="ui.text.primary"
        cursor="text"
        textOverflow="ellipsis"
        _hover={{ bg: 'ui.surface.input' }}
        _focus={{ boxShadow: 'none', bg: 'ui.surface.input' }}
      />
      {embedded ? null : (
        <IconButton
          aria-label="Edit doc title"
          title="Edit doc title"
          size="2xs"
          variant="ghost"
          color="ui.text.tertiary"
          onClick={() => titleInputRef.current?.focus()}
        >
          <Pencil size={12} />
        </IconButton>
      )}
    </HStack>
  );
  const toolbarNavigation = embedded ? (
    <HStack gap="1.5" minW={0} w="full">
      {embeddedNavigation}
      {titleEditor}
      {saveError ? (
        <Box
          boxSize="2"
          flexShrink={0}
          borderRadius="full"
          bg="status.danger.fg"
          title="Changes not saved"
        />
      ) : null}
    </HStack>
  ) : (
    <HStack gap="2" minW={0}>
      <IconButton asChild aria-label="Back to docs" size="sm" variant="ghost">
        <NextLink href="/app/docs">
          <ChevronLeft size={16} />
        </NextLink>
      </IconButton>
      {titleEditor}
      {saveError ? (
        <Text fontSize="xs" color="status.danger.fg" whiteSpace="nowrap">
          Not saved
        </Text>
      ) : null}
    </HStack>
  );
  const commentsToggleLabel = commentsPaneOpen
    ? 'Hide comments'
    : unresolvedCommentCount
      ? `Show comments, ${unresolvedCommentCount} unresolved`
      : 'Show comments';
  const toolbarActions = embedded ? (
    <HStack gap="1">
      {embeddedActions}
      <Box position="relative">
        <IconButton
          aria-label={commentsToggleLabel}
          title={commentsToggleLabel}
          size="sm"
          variant={commentsPaneOpen ? 'subtle' : 'ghost'}
          aria-pressed={commentsPaneOpen}
          onClick={() => setCommentsPaneOpen(!commentsPaneOpen)}
        >
          <MessageSquare size={16} />
        </IconButton>
        {unresolvedCommentCount ? (
          <Center
            position="absolute"
            top="-1"
            right="-1"
            minW="4"
            h="4"
            px="1"
            borderRadius="full"
            bg="status.danger.fg"
            color="white"
            fontSize="9px"
            fontWeight="800"
            pointerEvents="none"
          >
            {unresolvedCommentCount > 99 ? '99+' : unresolvedCommentCount}
          </Center>
        ) : null}
      </Box>
      <Menu.Root positioning={{ placement: 'bottom-end', gutter: 8 }}>
        <Menu.Trigger asChild>
          <IconButton
            aria-label="More doc actions"
            title="More doc actions"
            size="sm"
            variant="ghost"
          >
            <Ellipsis size={17} />
          </IconButton>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content layerStyle="surfacePopover" minW="210px" p="1.5" fontSize="13px">
              <Menu.Item
                value="history"
                disabled={collaborationEditingPaused}
                onSelect={() => void openHistory()}
              >
                <FileClock size={15} /> Version history
              </Menu.Item>
              {canManageReport ? (
                <>
                  <Menu.Item
                    value="schedule"
                    disabled={collaborationEditingPaused}
                    onSelect={() => setScheduleOpen(true)}
                  >
                    <CalendarClock size={15} /> Report schedule
                  </Menu.Item>
                  <Menu.Item
                    value="run"
                    disabled={collaborationEditingPaused || Boolean(refreshRunId)}
                    onSelect={() => void refreshReport()}
                  >
                    <RefreshCw size={15} /> {refreshRunId ? 'Running…' : 'Run now'}
                  </Menu.Item>
                </>
              ) : null}
              <Menu.Item value="open-full" asChild>
                <NextLink href={`/app/docs/${encodeURIComponent(reportId)}`}>
                  <ExternalLink size={15} /> Open full page
                </NextLink>
              </Menu.Item>
              <Menu.Separator />
              {REPORT_EXPORT_OPTIONS.map(option => (
                <Menu.Item
                  key={option.format}
                  value={`export-${option.format}`}
                  disabled={Boolean(exportingFormat)}
                  onSelect={() => void exportReport(option.format)}
                >
                  <Download size={15} /> {option.label}
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
      <SidePanelToggleButton open label="Collapse Docs & Assets" onClick={onClose} />
    </HStack>
  ) : (
    <HStack gap="1.5">
      <Button
        size="sm"
        fontSize="13px"
        variant={commentsPaneOpen ? 'subtle' : 'ghost'}
        minW={{ base: '9', xl: 'auto' }}
        px={{ base: '2', xl: '3' }}
        aria-label={commentsToggleLabel}
        title={commentsToggleLabel}
        aria-pressed={commentsPaneOpen}
        onClick={() => setCommentsPaneOpen(!commentsPaneOpen)}
      >
        <MessageSquare size={15} />
        <Text as="span" display={{ base: 'none', xl: 'inline' }}>
          {commentsPaneOpen
            ? 'Hide comments'
            : `Comments${unresolvedCommentCount ? ` (${unresolvedCommentCount})` : ''}`}
        </Text>
      </Button>
      <Button
        size="sm"
        fontSize="13px"
        variant="ghost"
        minW={{ base: '9', xl: 'auto' }}
        px={{ base: '2', xl: '3' }}
        aria-label="Version history"
        title="Version history"
        disabled={collaborationEditingPaused}
        onClick={() => void openHistory()}
      >
        <FileClock size={15} />
        <Text as="span" display={{ base: 'none', xl: 'inline' }}>
          History
        </Text>
      </Button>
      <Menu.Root positioning={{ placement: 'bottom-end', gutter: 8 }}>
        <Menu.Trigger asChild>
          <Button
            size="sm"
            fontSize="13px"
            variant="ghost"
            minW={{ base: '9', xl: 'auto' }}
            px={{ base: '2', xl: '3' }}
            aria-label="Export doc"
            title="Export doc"
            loading={Boolean(exportingFormat)}
          >
            <Download size={15} />
            <Text as="span" display={{ base: 'none', xl: 'inline' }}>
              Export
            </Text>
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content layerStyle="surfacePopover" minW="210px" p="1.5" fontSize="13px">
              {REPORT_EXPORT_OPTIONS.map(option => (
                <Menu.Item
                  key={option.format}
                  value={option.format}
                  onSelect={() => void exportReport(option.format)}
                >
                  {option.label}
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
      {canManageReport ? (
        <>
          <Button
            size="sm"
            fontSize="13px"
            variant="ghost"
            minW={{ base: '9', xl: 'auto' }}
            px={{ base: '2', xl: '3' }}
            aria-label="Report schedule"
            title="Report schedule"
            disabled={collaborationEditingPaused}
            onClick={() => setScheduleOpen(true)}
          >
            <CalendarClock size={15} />
            <Text as="span" display={{ base: 'none', xl: 'inline' }}>
              {schedule?.trigger?.type === 'weekly_scan'
                ? `Weekly ${WEEKDAYS[schedule.trigger.day_of_week ?? 1]}`
                : 'Schedule'}
            </Text>
          </Button>
          <Button
            size="sm"
            fontSize="13px"
            minW={{ base: '9', xl: 'auto' }}
            px={{ base: '2', xl: '3' }}
            aria-label="Run report now"
            title="Run report now"
            disabled={collaborationEditingPaused}
            onClick={() => void refreshReport()}
            loading={Boolean(refreshRunId)}
          >
            <RefreshCw size={15} />
            <Text as="span" display={{ base: 'none', xl: 'inline' }}>
              Run now
            </Text>
          </Button>
        </>
      ) : null}
    </HStack>
  );

  if (loadError) {
    return (
      <Flex direction="column" h="full" minH={0}>
        {embedded ? (
          <HStack
            minH="54px"
            px="2.5"
            py="2"
            gap="2"
            borderBottomWidth="1px"
            borderColor="ui.border.subtle"
            bg="ui.surface.rail"
            flexShrink={0}
          >
            {embeddedNavigation}
            <Text minW={0} flex="1" fontSize="13px" fontWeight="550" truncate>
              Doc unavailable
            </Text>
            <SidePanelToggleButton open label="Collapse Docs & Assets" onClick={onClose} />
          </HStack>
        ) : null}
        <Center flex="1" minH="0" px="4">
          <VStack gap="4" maxW="440px" px="6" py="8" textAlign="center" layerStyle="surfacePanel">
            <Box>
              <Text fontWeight="750" color="ui.text.primary">
                Couldn’t load this doc
              </Text>
              <Text mt="1" fontSize="sm" color="ui.text.secondary">
                {loadError}
              </Text>
            </Box>
            <HStack gap="2">
              {embedded ? (
                <Button size="sm" variant="outline" onClick={onClose}>
                  Close
                </Button>
              ) : (
                <Button size="sm" variant="outline" asChild>
                  <NextLink href="/app/docs">Back to docs</NextLink>
                </Button>
              )}
              <Button size="sm" onClick={() => setLoadRetryTick(current => current + 1)}>
                <RefreshCw size={15} /> Retry
              </Button>
            </HStack>
          </VStack>
        </Center>
      </Flex>
    );
  }

  if (loading || !report || !content) {
    return (
      <Flex direction="column" h="full" minH={0}>
        {embedded ? (
          <HStack
            minH="54px"
            px="2.5"
            py="2"
            gap="2"
            borderBottomWidth="1px"
            borderColor="ui.border.subtle"
            bg="ui.surface.rail"
            flexShrink={0}
          >
            {embeddedNavigation}
            <Text fontSize="sm" color="ui.text.secondary">
              Docs
            </Text>
            <Box flex="1" />
            <SidePanelToggleButton open label="Collapse Docs & Assets" onClick={onClose} />
          </HStack>
        ) : (
          <AppShellToolbarPortal>
            <Text fontSize="sm" fontWeight="650">
              Docs
            </Text>
          </AppShellToolbarPortal>
        )}
        <Box flex="1" minH={0} overflow="hidden">
          <LoadingState message="Loading doc…" minH="520px" />
        </Box>
      </Flex>
    );
  }

  const unresolvedComments = comments.filter(comment => !comment.resolved);

  const commentsPanel = (
    <Box
      as="aside"
      aria-label="Report comments"
      w="full"
      flexShrink={0}
      position="static"
      alignSelf="flex-start"
      h="full"
      maxH="full"
      overflowY="auto"
      bg="ui.surface.card"
      borderRadius="none"
      boxShadow="none"
    >
      <HStack
        px="4"
        py="3"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        bg="ui.surface.card"
      >
        <MessageSquare size={15} />
        <Text fontSize="sm" fontWeight="750">
          Comments
        </Text>
        <Box flex="1" />
        <Text fontSize="2xs" color="ui.text.tertiary">
          {unresolvedComments.length || 'No'} open
        </Text>
        <IconButton
          aria-label="Hide comments"
          title="Hide comments"
          size="2xs"
          variant="ghost"
          onClick={() => setCommentsPaneOpen(false)}
        >
          <X size={13} />
        </IconButton>
      </HStack>

      <VStack align="stretch" gap="2" p="2.5">
        {unresolvedComments.length ? (
          unresolvedComments.map(comment => {
            const selected = comment.id === activeCommentId;
            const firstMessage = comment.messages[0];
            return (
              <Box
                key={comment.id}
                ref={selected ? activeCommentCardRef : undefined}
                p="3"
                borderWidth="1px"
                borderColor={selected ? 'ui.border.strong' : 'ui.border.subtle'}
                borderRadius="glassInput"
                bg={selected ? 'ui.surface.selected' : 'ui.surface.card'}
                boxShadow={selected ? 'var(--attune-shadow-subtle, none)' : 'none'}
                transition="border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease"
                _hover={{ borderColor: 'ui.border.strong', transform: 'translateY(-1px)' }}
              >
                <HStack
                  as="button"
                  w="full"
                  align="start"
                  gap="2.5"
                  textAlign="left"
                  cursor="pointer"
                  aria-pressed={selected}
                  onClick={() => {
                    setCommentBlockId(comment.blockId);
                    setCommentAnchor(comment.anchor);
                    setActiveCommentId(comment.id);
                  }}
                >
                  <Avatar.Root size="2xs" flexShrink={0}>
                    <Avatar.Fallback
                      bg="accent.subtle"
                      color="accent.fg"
                      fontSize="2xs"
                      fontWeight="750"
                    >
                      {firstMessage?.authorName.slice(0, 2).toUpperCase() || 'CO'}
                    </Avatar.Fallback>
                  </Avatar.Root>
                  <Box minW={0} flex="1">
                    <HStack justify="space-between" gap="2">
                      <Text fontSize="xs" fontWeight="750" truncate>
                        {firstMessage?.authorName || 'Collaborator'}
                      </Text>
                      <Text fontSize="2xs" color="ui.text.tertiary" whiteSpace="nowrap">
                        {formatCodexRelativeTime(comment.updatedAt)}
                      </Text>
                    </HStack>
                    <Text mt="1" fontSize="xs" lineHeight="1.5" color="ui.text.secondary">
                      {firstMessage?.body}
                    </Text>
                    {comment.anchor ? (
                      <Text
                        mt="1.5"
                        px="2"
                        py="1"
                        borderLeftWidth="2px"
                        borderColor="ui.border.accent"
                        bg="accent.subtle"
                        fontSize="2xs"
                        color="ui.text.secondary"
                        lineClamp={2}
                      >
                        “{comment.anchor.quote}”
                      </Text>
                    ) : null}
                    {comment.messages.length > 1 ? (
                      <Text mt="1.5" fontSize="2xs" color="ui.text.tertiary">
                        {comment.messages.length - 1}{' '}
                        {comment.messages.length === 2 ? 'reply' : 'replies'}
                      </Text>
                    ) : null}
                  </Box>
                </HStack>

                {selected ? (
                  <VStack
                    align="stretch"
                    gap="2.5"
                    mt="3"
                    pt="3"
                    borderTopWidth="1px"
                    borderColor="ui.border.subtle"
                    onClick={event => event.stopPropagation()}
                  >
                    {comment.messages.slice(1).map(message => (
                      <Box key={message.id} pl="8">
                        <HStack justify="space-between" gap="2">
                          <Text fontSize="2xs" fontWeight="750">
                            {message.authorName}
                          </Text>
                          <Text fontSize="2xs" color="ui.text.tertiary">
                            {formatCodexRelativeTime(message.createdAt)}
                          </Text>
                        </HStack>
                        <Text mt="0.5" fontSize="xs" color="ui.text.secondary">
                          {message.body}
                        </Text>
                      </Box>
                    ))}
                    <HStack gap="1.5">
                      <Input
                        size="xs"
                        value={commentReplies[comment.id] || ''}
                        placeholder="Reply…"
                        onChange={event =>
                          setCommentReplies(current => ({
                            ...current,
                            [comment.id]: event.target.value,
                          }))
                        }
                        onKeyDown={event => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            void replyToComment(comment.id);
                          }
                        }}
                      />
                      <Button
                        size="xs"
                        disabled={!String(commentReplies[comment.id] || '').trim()}
                        onClick={() => void replyToComment(comment.id)}
                      >
                        Reply
                      </Button>
                    </HStack>
                    <Button
                      size="xs"
                      variant="ghost"
                      color="ui.text.tertiary"
                      alignSelf="flex-end"
                      onClick={() => void resolveComment(comment.id)}
                    >
                      Resolve thread
                    </Button>
                  </VStack>
                ) : null}
              </Box>
            );
          })
        ) : (
          <Box px="3" py="6" textAlign="center">
            <Text fontSize="sm" fontWeight="700">
              All clear
            </Text>
            <Text mt="1" fontSize="xs" color="ui.text.tertiary">
              Select text or place the cursor in a block, then comment from the editor toolbar.
            </Text>
          </Box>
        )}

        {commentBlockId ? (
          <VStack
            align="stretch"
            gap="2"
            mt="1"
            p="3"
            bg="ui.surface.input"
            borderRadius="glassInput"
          >
            <Text fontSize="xs" fontWeight="750">
              {commentAnchor ? 'New comment on selected text' : 'New comment on selected block'}
            </Text>
            {commentAnchor ? (
              <Text
                px="2"
                py="1.5"
                borderLeftWidth="2px"
                borderColor="ui.border.accent"
                bg="accent.subtle"
                fontSize="xs"
                color="ui.text.secondary"
                lineClamp={3}
              >
                “{commentAnchor.quote}”
              </Text>
            ) : null}
            <Textarea
              size="sm"
              value={commentDraft}
              onChange={event => setCommentDraft(event.target.value)}
              placeholder="Write a comment…"
              minH="82px"
              bg="ui.surface.card"
            />
            <Button
              size="xs"
              alignSelf="flex-end"
              onClick={() => void createComment()}
              loading={commentSaving}
              disabled={!commentDraft.trim()}
            >
              <MessageSquare size={13} /> Comment
            </Button>
          </VStack>
        ) : null}
      </VStack>
    </Box>
  );

  const commentsSplitQuery = embedded
    ? '@container (min-width: 760px)'
    : '@container (min-width: 900px)';

  return (
    <Flex
      data-voice-document-id={report.id}
      direction="column"
      w="full"
      h="full"
      minH={0}
      overflow="hidden"
      position="relative"
    >
      {embedded ? (
        <HStack
          minH="54px"
          px="2.5"
          py="2"
          gap="2"
          justify="space-between"
          borderBottomWidth="1px"
          borderColor="ui.border.subtle"
          bg="ui.surface.rail"
          flexShrink={0}
        >
          <Box minW={0} flex="1">
            {toolbarNavigation}
          </Box>
          <Box flexShrink={0}>{toolbarActions}</Box>
        </HStack>
      ) : (
        <AppShellToolbarPortal navigation={toolbarNavigation} actions={toolbarActions} />
      )}
      <Flex
        position="relative"
        containerType="inline-size"
        flex="1"
        minH={0}
        overflow="hidden"
        bg="ui.surface.card"
        data-section="report-with-comments"
      >
        <Box
          flex="1"
          minW={0}
          overflowY="auto"
          px={embedded ? { base: 2.5, md: 4 } : { base: 3, md: 6, xl: 10 }}
          py={embedded ? { base: 2.5, md: 3 } : { base: 3, md: 4 }}
          data-section="report-document-surface"
        >
          <Box maxW={embedded ? '960px' : '1280px'} mx="auto">
            {report.status === 'update_failed' ? (
              <HStack
                mb="3"
                px="3.5"
                py="2.5"
                borderRadius="glassInput"
                borderWidth="1px"
                borderColor="status.warning.border"
                bg="status.warning.bg"
                color="status.warning.fg"
                justify="space-between"
              >
                <Text fontSize="sm">
                  The latest task answer is safe, but this report is retrying its automatic update.
                </Text>
                {canManageReport ? (
                  <Button size="xs" variant="ghost" onClick={() => void refreshReport()}>
                    Retry now
                  </Button>
                ) : null}
              </HStack>
            ) : report.lastAgentUpdateAt ? (
              <HStack
                mb="3"
                px="3.5"
                py="2.5"
                borderRadius="glassInput"
                borderWidth="1px"
                borderColor="status.success.border"
                bg="status.success.bg"
                color="status.success.fg"
                justify="space-between"
              >
                <Text fontSize="sm">
                  Attune refreshed this report {formatCodexRelativeTime(report.lastAgentUpdateAt)} ·
                  Version {report.currentVersion}
                </Text>
                <Button size="xs" variant="ghost" onClick={() => void openHistory()}>
                  View history
                </Button>
              </HStack>
            ) : null}

            <Flex direction={{ base: 'column', xl: 'row' }} align="flex-start" gap="4">
              <Box minW={0} flex="1" w="full">
                <ReportEditor
                  contentReaderRef={liveContentReaderRef}
                  reportId={report.id}
                  initialContent={content}
                  collaboration={collaboration}
                  onChange={scheduleSave}
                  onRequestComment={requestComment}
                  onSelectComment={commentId => {
                    setActiveCommentId(commentId);
                    setCommentsPaneOpen(true);
                    requestAnimationFrame(() => {
                      activeCommentCardRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                      });
                    });
                  }}
                  onPresenceChange={setPresenceUsers}
                  onConnectionStatusChange={setCollaborationStatus}
                  comments={unresolvedComments}
                  activeCommentId={activeCommentId}
                />
              </Box>
            </Flex>

            {canManageReport ? (
              <HStack justify="flex-end" mt="4" pb="6">
                <Button
                  size="xs"
                  variant="ghost"
                  color="status.danger.fg"
                  onClick={() => void deleteReport()}
                  loading={deleting}
                >
                  <Trash2 size={13} /> Archive doc
                </Button>
              </HStack>
            ) : null}
          </Box>
          {commentsPaneOpen ? (
            <Box
              aria-hidden="true"
              h="min(58dvh, 520px)"
              css={{ [commentsSplitQuery]: { height: 0 } }}
              data-section="report-comments-scroll-spacer"
            />
          ) : null}
        </Box>
        {commentsPaneOpen ? (
          <Box
            position="absolute"
            right="0"
            bottom="0"
            zIndex="docked"
            w="full"
            h="min(58dvh, 520px)"
            flexShrink={0}
            minH={0}
            overflow="hidden"
            bg="ui.surface.card"
            borderTopWidth="1px"
            borderLeftWidth={0}
            borderColor="ui.border.subtle"
            boxShadow="var(--attune-shadow-elevated, none)"
            css={{
              [commentsSplitQuery]: {
                position: 'static',
                zIndex: 'auto',
                width: embedded ? 'min(340px, 42%)' : '360px',
                height: '100%',
                borderTopWidth: 0,
                borderLeftWidth: '1px',
                boxShadow: 'none',
              },
            }}
            data-section="report-comments-surface"
          >
            {commentsPanel}
          </Box>
        ) : null}
      </Flex>

      <Drawer.Root
        open={historyOpen}
        onOpenChange={event => setHistoryOpen(event.open)}
        placement="end"
        size="md"
      >
        <Portal>
          <Drawer.Backdrop bg="ui.overlay.backdrop" />
          <Drawer.Positioner>
            <Drawer.Content layerStyle="drawerSurface">
              <Drawer.Header borderBottomWidth="1px" borderColor="ui.border.subtle">
                <Drawer.Title>Version history</Drawer.Title>
                <Drawer.CloseTrigger asChild>
                  <IconButton aria-label="Close history" size="sm" variant="ghost">
                    <X size={16} />
                  </IconButton>
                </Drawer.CloseTrigger>
              </Drawer.Header>
              <Drawer.Body p="3">
                <VStack align="stretch" gap="2">
                  {versions.map(version => (
                    <Box
                      key={version.id}
                      p="3"
                      borderWidth="1px"
                      borderColor="ui.border.subtle"
                      borderRadius="glassInput"
                      bg="ui.surface.card"
                    >
                      <HStack justify="space-between" align="start">
                        <Box>
                          <Text fontSize="sm" fontWeight="720">
                            Version {version.version}
                          </Text>
                          <Text mt="0.5" fontSize="xs" color="ui.text.tertiary">
                            {version.summary}
                          </Text>
                        </Box>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => void restoreVersion(version)}
                        >
                          <RotateCcw size={13} /> Restore
                        </Button>
                      </HStack>
                      <Text mt="2" fontSize="2xs" color="ui.text.tertiary">
                        {version.actorType === 'agent' ? 'Attune agent' : 'Workspace editor'} ·{' '}
                        {formatCodexRelativeTime(version.createdAt)}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      <Dialog.Root
        open={canManageReport && scheduleOpen}
        onOpenChange={event => {
          if (canManageReport) setScheduleOpen(event.open);
        }}
        placement="center"
      >
        <Portal>
          <Dialog.Backdrop bg="ui.overlay.backdrop" />
          <Dialog.Positioner>
            <Dialog.Content layerStyle="surfacePopover" maxW="480px">
              <Dialog.Header>
                <Dialog.Title>Schedule this doc</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <IconButton aria-label="Close schedule" size="sm" variant="ghost">
                    <X size={16} />
                  </IconButton>
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <VStack align="stretch" gap="4">
                  <Text fontSize="sm" color="ui.text.secondary">
                    Attune will use this doc as the objective and template, then create a separate
                    dated doc from every background-task run. This original is never overwritten.
                  </Text>
                  <HStack gap="2">
                    {[
                      ['manual', 'Manual'],
                      ['weekly_scan', 'Weekly'],
                      ['monthly_scan', 'Monthly'],
                    ].map(([value, label]) => (
                      <Button
                        key={value}
                        size="sm"
                        variant={scheduleType === value ? 'solid' : 'outline'}
                        onClick={() => setScheduleType(value as typeof scheduleType)}
                      >
                        {label}
                      </Button>
                    ))}
                  </HStack>
                  {scheduleType === 'weekly_scan' ? (
                    <Box>
                      <Text mb="2" fontSize="xs" fontWeight="700" color="ui.text.secondary">
                        Run on
                      </Text>
                      <HStack gap="1" flexWrap="wrap">
                        {WEEKDAYS.map((day, index) => (
                          <Button
                            key={day}
                            size="xs"
                            variant={scheduleDay === index ? 'solid' : 'ghost'}
                            onClick={() => setScheduleDay(index)}
                          >
                            {day}
                          </Button>
                        ))}
                      </HStack>
                    </Box>
                  ) : scheduleType === 'monthly_scan' ? (
                    <Box>
                      <Text mb="2" fontSize="xs" fontWeight="700" color="ui.text.secondary">
                        Day of month
                      </Text>
                      <Input
                        type="number"
                        min={1}
                        max={31}
                        value={scheduleDay}
                        onChange={event =>
                          setScheduleDay(Math.max(1, Math.min(31, Number(event.target.value) || 1)))
                        }
                      />
                    </Box>
                  ) : null}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" onClick={() => setScheduleOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => void saveSchedule()} loading={scheduleSaving}>
                  Save schedule
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Flex>
  );
}
